import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

axios.defaults.withCredentials = true;

// Auto-refresh on 401 — try the refresh endpoint once and replay the
// original request. We only SKIP refresh for the auth endpoints that
// would cause loops or are meaningless to retry:
//   • /auth/login   — a 401 here means bad credentials, not expired token
//   • /auth/refresh — a 401 here means the refresh token itself is dead
//   • /auth/logout  — no point retrying after logout
// Every other endpoint (including /auth/me, which is called on every page
// load) will trigger a silent refresh so the user stays signed in for the
// full lifetime of their refresh token.
axios.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;
    const url = originalRequest?.url || '';
    const isSkippableAuthUrl = /\/auth\/(login|refresh|logout)(\b|$)/.test(url);
    if (error.response?.status === 401 && !originalRequest._retry && !isSkippableAuthUrl) {
      originalRequest._retry = true;
      try {
        await axios.post(`${API}/auth/refresh`);
        return axios(originalRequest);
      } catch {
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    // Admin 2FA: if the server requires an OTP, we have NOT yet received
    // real auth cookies — just a challenge handle. The caller is
    // responsible for prompting for the OTP and calling `verifyOtp()`.
    if (data?.otp_required) return data;
    setUser(data);
    return data;
  };

  const verifyOtp = async (challenge_id, otp, remember_device = false) => {
    const { data } = await axios.post(
      `${API}/auth/login/verify-otp`,
      { challenge_id, otp, remember_device },
    );
    setUser(data);
    return data;
  };

  const resendOtp = async (challenge_id) => {
    const { data } = await axios.post(`${API}/auth/login/resend-otp`, { challenge_id });
    return data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
  };

  const value = {
    user,
    setUser,
    loading,
    login,
    verifyOtp,
    resendOtp,
    logout,
    checkAuth,
    isAdmin: user?.role === 'admin',
    isGM: user?.role === 'general_manager' || !!user?.is_general_manager,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
